<template>
  <div class="font-sans bg-[#f6f4f1] text-[#1f2937] min-h-screen flex flex-col">
    <!-- 🌟 HEADER -->
    <header class="bg-[#1a1a1a] text-white shadow sticky top-0 z-50">
      <div class="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
        <router-link to="/" class="flex items-center gap-2 cursor-pointer">
          <img src="/brand/getlance-logo.svg" alt="Logo" class="h-20 w-auto" />
        </router-link>

        <div class="flex gap-3">
          <router-link to="/cadastro"
            class="px-4 py-2 rounded-full border border-white/20 bg-white/10 hover:bg-white/20 transition whitespace-nowrap">
            Criar conta
          </router-link>
        </div>
      </div>
    </header>

    <!-- 🔐 LOGIN -->
    <section class="flex-1 flex items-center justify-center px-6 py-16">
      <div class="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-[#e0dcd6] overflow-hidden">
        <!-- TÍTULO CENTRALIZADO -->
        <div class="text-center mb-8">
          <h2 class="text-3xl font-bold text-[#152a34] mb-1">Bem-vindo de volta</h2>
          <p class="text-gray-500">Acesse sua conta para continuar.</p>
        </div>

        <!-- GOOGLE LOGIN (✅ responsivo, não estoura no mobile) -->
        <div class="mb-6">
          <div ref="googleBtnWrap" class="w-full overflow-hidden">
            <div ref="googleBtn" class="w-full flex justify-center"></div>
          </div>
        </div>

        <!-- SEPARADOR -->
        <div class="flex items-center my-6">
          <div class="flex-grow border-t border-gray-300"></div>
          <span class="mx-3 text-gray-500 text-sm">ou</span>
          <div class="flex-grow border-t border-gray-300"></div>
        </div>

        <!-- FORM -->
        <form @submit.prevent="login">
          <!-- EMAIL -->
          <div class="mb-4">
            <label class="block text-sm font-medium mb-1">E-mail</label>
            <input v-model="email" type="email" placeholder="seuemail@exemplo.com" class="input-gold" />
          </div>

          <!-- SENHA -->
          <div class="mb-4">
            <label class="block text-sm font-medium mb-1">Senha</label>

            <div class="relative">
              <input v-model="password" :type="passwordVisible ? 'text' : 'password'" placeholder="********"
                class="input-gold pr-10" />

              <button type="button" @click="passwordVisible = !passwordVisible"
                class="absolute inset-y-0 right-3 flex items-center text-gray-500">
                <i :class="passwordVisible ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
              </button>
            </div>
          </div>

          <!-- BOTÃO ENTRAR -->
          <button type="submit"
            class="w-full py-3 rounded-full bg-[#b99a52] hover:bg-[#a18446] text-white font-semibold transition disabled:opacity-50"
            :disabled="loading">
            <span v-if="!loading">Entrar</span>
            <span v-else>Aguarde...</span>
          </button>
        </form>

        <!-- LINKS -->
        <div class="flex justify-between mt-6 text-sm">
          <button @click="goToForgotPassword" class="text-[#152a34] hover:underline">
            Esqueceu a senha?
          </button>

          <button @click="goToRegister" class="text-[#b99a52] font-semibold hover:underline">
            Criar conta
          </button>
        </div>

        <!-- TOAST -->
        <transition name="fade">
          <div v-if="toast.show" class="mt-6 p-3 rounded-lg text-white text-center"
            :class="toast.color === 'error' ? 'bg-[#ef4444]' : 'bg-[#10b981]'">
            {{ toast.message }}
          </div>
        </transition>
      </div>
    </section>

    <AppFooter />
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick, onBeforeUnmount } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useStore } from "vuex";
import Swal from "sweetalert2";
import AppFooter from "@/components/AppFooter.vue";
import api from "@/services/api";
import { useProviderOnboardingGuard } from "@/composables/useProviderOnboardingGuard";

const { enforcePresentialOnboarding } = useProviderOnboardingGuard();

const email = ref("");
const password = ref("");
const passwordVisible = ref(false);
const loading = ref(false);

const toast = ref({
  show: false,
  message: "",
  color: "error",
});

const router = useRouter();
const route = useRoute();
const store = useStore();

const GOOGLE_CLIENT_ID =
  "708117913743-j1tmpf9b8endvjd3im363cniugltsvjd.apps.googleusercontent.com";

const googleBtn = ref(null);
const googleBtnWrap = ref(null);

const showToast = (message, color = "error") => {
  toast.value = { show: true, message, color };
  setTimeout(() => (toast.value.show = false), 3000);
};

const goToRegister = () => router.push("/cadastro");
const goToForgotPassword = () => router.push("/esqueceu-senha");

/** espera Google Identity carregar */
const waitForGoogle = (timeoutMs = 4000) => {
  return new Promise((resolve, reject) => {
    const start = Date.now();
    const tick = () => {
      if (window.google?.accounts?.id) return resolve(true);
      if (Date.now() - start >= timeoutMs) return reject(new Error("timeout"));
      setTimeout(tick, 50);
    };
    tick();
  });
};

/** ✅ render responsivo: não estoura no mobile */
function renderGoogleButton() {
  if (!googleBtn.value || !googleBtnWrap.value) return;

  const wrapWidth = googleBtnWrap.value.clientWidth || 0;
  const buttonWidth = Math.min(360, Math.max(240, wrapWidth)); // min 240 / max 360

  // evita duplicar o botão ao re-render
  googleBtn.value.innerHTML = "";

  window.google.accounts.id.renderButton(googleBtn.value, {
    theme: "outline",
    size: "large",
    text: "signin_with",
    shape: "pill",
    width: buttonWidth,
    logo_alignment: "left",
  });
}

onMounted(async () => {
  await nextTick();

  if (route.query.reason === "pending") {
    Swal.fire({
      icon: "warning",
      title: "Conta pendente",
      text: "Confirme seu e-mail para ativar a conta antes de continuar.",
    });
  }

  try {
    await waitForGoogle(5000);
  } catch {
    Swal.fire({
      icon: "error",
      title: "Erro",
      text: "Google Sign-In não carregou a tempo. Recarregue a página.",
    });
    return;
  }

  if (!googleBtn.value || !googleBtnWrap.value) {
    Swal.fire({
      icon: "error",
      title: "Erro",
      text: "Container do botão Google não foi encontrado (refs googleBtn/googleBtnWrap).",
    });
    return;
  }

  window.google.accounts.id.initialize({
    client_id: GOOGLE_CLIENT_ID,
    callback: handleGoogleCallback,
  });

  renderGoogleButton();
  window.addEventListener("resize", renderGoogleButton);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", renderGoogleButton);
});

const login = async () => {
  if (!email.value || !password.value) {
    Swal.fire({
      icon: "warning",
      title: "Atenção",
      text: "Preencha todos os campos.",
    });
    return;
  }

  loading.value = true;

  try {
    const response = await store.dispatch("postLogin", {
      email: email.value,
      password: password.value,
    });

    if (!response.user || !response.token) return showToast("Falha ao recuperar sessão.");

    // 1) Admin → dashboard
    if (!["user", ""].includes(response.user.role)) {
      router.push("/dashboard");
      return;
    }

    // 2) Onboarding obrigatório para presencial/ambos (serviço + agenda)
    const redirected = await enforcePresentialOnboarding();
    if (redirected) return;

    // 3) Fluxo normal — respeita ?redirect= se houver
    router.push(route.query.redirect || "/");
  } catch (error) {
    const code = error.response?.data?.code;
    if (code === "USER_NOT_ACTIVATED") {
      Swal.fire({
        icon: "warning",
        title: "Conta pendente",
        html: `Enviamos um e-mail para <b>${email.value}</b>.<br/>Confira a caixa de entrada e o spam.`,
      });
      return;
    }
    Swal.fire({
      icon: "error",
      text: error.response?.data?.error || "Erro ao entrar.",
    });
  } finally {
    loading.value = false;
  }
};

const handleGoogleCallback = async (response) => {
  try {
    loading.value = true;

    const result = await store.dispatch("signInWithGoogle", {
      credential: response.credential,
    });

    if (!result?.token) {
      Swal.fire({ icon: "error", title: "Erro ao entrar com Google.", text: "Resposta inválida do servidor. Tente novamente." });
      return;
    }

    if (result.is_new_user) {
      await runProviderOnboarding(result);
      return;
    }

    // Usuário existente — checa onboarding obrigatório para presencial/ambos
    const redirected = await enforcePresentialOnboarding();
    if (redirected) return;

    router.push("/");
  } catch (error) {
    console.error("Erro ao autenticar com Google:", error);
    const code = error.response?.data?.code;
    if (code === "USER_NOT_ACTIVATED") {
      Swal.fire({ icon: "warning", title: "Conta pendente", text: "Confira a caixa de entrada e o spam do e-mail utilizado no Google." });
      return;
    }
    Swal.fire({ icon: "error", title: "Erro ao entrar com Google.", text: error.response?.data?.error || "Tente novamente mais tarde." });
  } finally {
    loading.value = false;
  }
};

async function runProviderOnboarding(result) {
  // Etapa 1: escolher tipo de atuação
  const { value: providerType } = await Swal.fire({
    title: 'Bem-vindo à GetLance!',
    html: `
      <p class="text-sm text-gray-500 mb-4">Como você vai usar a plataforma?</p>
      <div style="display:flex;flex-direction:column;gap:8px;text-align:left">
        <label style="display:flex;align-items:flex-start;gap:10px;padding:12px;border-radius:10px;border:2px solid #e5e7eb;cursor:pointer" id="lbl-remote">
          <input type="radio" name="pt" value="remote" style="margin-top:3px" checked />
          <div>
            <strong style="display:block;font-size:14px">Somente Remoto</strong>
            <span style="font-size:12px;color:#6b7280">Projetos e serviços remotos (design, dev, etc.)</span>
          </div>
        </label>
        <label style="display:flex;align-items:flex-start;gap:10px;padding:12px;border-radius:10px;border:2px solid #e5e7eb;cursor:pointer" id="lbl-presential">
          <input type="radio" name="pt" value="presential" style="margin-top:3px" />
          <div>
            <strong style="display:block;font-size:14px">Somente Presencial</strong>
            <span style="font-size:12px;color:#6b7280">Serviços presenciais (personal trainer, eletricista, etc.)</span>
          </div>
        </label>
      </div>
    `,
    confirmButtonText: 'Continuar',
    confirmButtonColor: '#b99a52',
    allowOutsideClick: false,
    preConfirm: () => {
      const selected = document.querySelector('input[name="pt"]:checked');
      return selected ? selected.value : 'remote';
    }
  });

  const selectedType = providerType || 'remote';
  let serviceAddress = null;
  let latitude = null;
  let longitude = null;

  // Etapa 2: CEP (somente se presential)
  if (selectedType === 'presential') {
    const { value: cepValue } = await Swal.fire({
      title: 'Informe seu CEP de atuação',
      html: `
        <p class="text-sm text-gray-500 mb-3">Para aparecer nas buscas de serviços presenciais, precisamos saber onde você atua.</p>
        <input id="swal-cep" type="text" maxlength="9" placeholder="00000-000"
          style="width:100%;padding:10px 14px;border:1.5px solid #e5e7eb;border-radius:10px;font-size:15px;outline:none"
          oninput="this.value=this.value.replace(/\\D/g,'').replace(/(\\d{5})(\\d)/,'$1-$2').slice(0,9)"
        />
        <p id="swal-cep-msg" style="font-size:12px;margin-top:8px;min-height:20px"></p>
        <div id="swal-cep-preview" style="display:none;margin-top:8px;padding:10px;background:#f0fdf4;border:1px solid #86efac;border-radius:8px;font-size:13px;color:#166534;text-align:left"></div>
      `,
      confirmButtonText: 'Confirmar endereço e Entrar',
      confirmButtonColor: '#b99a52',
      allowOutsideClick: false,
      didOpen: () => {
        const input = document.getElementById('swal-cep');
        const msg = document.getElementById('swal-cep-msg');
        const preview = document.getElementById('swal-cep-preview');
        if (input && msg) {
          input.addEventListener('input', () => {
            msg.textContent = '';
            msg.style.color = '';
            if (preview) { preview.style.display = 'none'; preview.textContent = ''; }
          });
        }
      },
      preConfirm: async () => {
        const cepInput = document.getElementById('swal-cep');
        const cep = (cepInput?.value || '').replace(/\D/g, '');
        const msg = document.getElementById('swal-cep-msg');
        const preview = document.getElementById('swal-cep-preview');

        const setError = (text) => {
          if (msg) { msg.textContent = text; msg.style.color = '#ef4444'; }
          if (preview) { preview.style.display = 'none'; preview.textContent = ''; }
        };
        const setSuccess = (text) => {
          if (msg) { msg.textContent = ''; msg.style.color = ''; }
          if (preview) { preview.textContent = text; preview.style.display = 'block'; preview.style.color = '#166534'; }
        };

        if (cep.length !== 8) {
          setError('Informe um CEP válido com 8 dígitos.');
          return false;
        }

        Swal.showLoading();
        try {
          const resp = await fetch(`${process.env.VUE_APP_URL_API}/cep?cep=${cep}`, {
            headers: { 'x-api-key': process.env.VUE_APP_API_KEY || '' }
          });
          const data = await resp.json();

          if (!data || data.erro) {
            Swal.hideLoading();
            setError('CEP não encontrado. Verifique e tente novamente.');
            return false;
          }

          const city = (data.localidade || '').trim();
          const state = (data.uf || '').trim();
          if (!city || !state) {
            Swal.hideLoading();
            setError('Endereço incompleto para este CEP. Não foi possível obter cidade/estado. Tente outro CEP.');
            return false;
          }

          const address = {
            zip_code: cep,
            address: (data.logradouro || '').trim(),
            neighborhood: (data.bairro || '').trim(),
            city,
            state,
          };

          const previewParts = [
            address.address && `Logradouro: ${address.address}`,
            address.neighborhood && `Bairro: ${address.neighborhood}`,
            `${address.city}/${address.state}`,
          ].filter(Boolean);
          const previewText = '✓ Endereço validado: ' + previewParts.join(' — ');
          setSuccess(previewText);
          Swal.hideLoading();
          return address;
        } catch {
          Swal.hideLoading();
          setError('Erro ao consultar o CEP. Verifique sua conexão e tente novamente.');
          return false;
        }
      }
    });

    if (cepValue) {
      serviceAddress = cepValue;
      // Tenta geocodificar para lat/lng
      const key = process.env.VUE_APP_GOOGLE_MAPS_API_KEY;
      if (key) {
        try {
          const addressStr = [serviceAddress.address, serviceAddress.neighborhood, serviceAddress.city, serviceAddress.state, 'Brasil'].filter(Boolean).join(', ');
          const geoRes = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(addressStr)}&key=${key}&language=pt-BR`);
          const geoData = await geoRes.json();
          if (geoData.status === 'OK' && geoData.results?.[0]) {
            const loc = geoData.results[0].geometry.location;
            latitude = loc.lat;
            longitude = loc.lng;
          }
        } catch {}
      }
    }
  }

  // Salvar tipo de atuação e localização
  try {
    const payload = { provider_type: selectedType };
    if (serviceAddress) payload.service_address = serviceAddress;
    if (latitude) payload.latitude = latitude;
    if (longitude) payload.longitude = longitude;
    await api.put('/provider/location', payload);

    // Atualizar localStorage com os novos dados
    const stored = JSON.parse(localStorage.getItem('userData') || '{}');
    if (stored.user) {
      stored.user.provider_type = selectedType;
      stored.user.latitude = latitude;
      stored.user.longitude = longitude;
      localStorage.setItem('userData', JSON.stringify(stored));
    }
  } catch {}

  // Para presencial, força onboarding (serviço + agenda) — usuário acabou de ser criado
  const redirected = await enforcePresentialOnboarding();
  if (redirected) return;

  router.push("/");
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>